"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.classifyAssistantTask = classifyAssistantTask;
exports.isFocusedAttachedDatabaseSchemaEdit = isFocusedAttachedDatabaseSchemaEdit;
const READ_ONLY_PATTERN = /\b(beschreib\w*|check|describe|erklär\w*|explain|inspect|lies|liste|list|read|show|status|summary|summari[sz]e|was ist|what is|zeige|zusammenfass\w*)\b/iu;
const MUTATION_PATTERN = /\b(add|ändere|änder\w*|anwenden|apply|bau\w*|bind|build\w*|connect\w*|configur\w*|create|edit|erstell\w*|fix|implement\w*|konfigurier\w*|lege|mach|publish|reparier\w*|setup|set up|test|umsetz\w*|verdraht\w*|wire)\b/iu;
const GERMAN_EDIT_PATTERN = /(?:^|[\s"'“”])(?:ändere|änder\w*)\b/iu;
const AUTOMATION_PATTERN = /\b(\w*automation\w*|automatisier\w*|automatisierung|workflow|blueprint|target runtime|goal runtime|runbook|swr)\b/iu;
const AUTOMATION_FLOW_PATTERN = /\b(input|output|quelle|source|target|trigger|ziel|node|knoten|tabelle|database|datenbank)\b/iu;
const CUSTOM_APP_PATTERN = /\b(app block|appblock|app-block|app dx|appdx|custom app|custom-app|view-backed app|anwendung|app|formular|dashboard|portal|seite|page|publish|veröffentl\w*)\b/iu;
const CUSTOM_APP_LIST_PATTERN = /(?:\b(apps|custom apps|anwendungen)\b.*\b(list|liste|auflisten|anzeigen|zeige|show)\b|\b(list|liste|auflisten|anzeigen|zeige|show)\b.*\b(apps|custom apps|anwendungen)\b)/iu;
const PUBLISH_PATTERN = /\b(publish|veröffentl\w*)\b/iu;
const TEST_PATTERN = /\b(test|smoke|prüf\w*|validate|validier\w*)\b/iu;
const BIND_PATTERN = /\b(bind|rebind|verbinde|anbind\w*|database|databaseId|datenbank)\b/iu;
const UUID_PATTERN = /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/giu;
const EXPLICIT_TARGET_ID_PATTERN = /\b(customAppId|appBlockId|databaseId|automationId|docId|documentId)\s*[:=]\s*([A-Za-z0-9_$:-]{3,})\b/gu;
const QUOTED_TARGET_PATTERN = /["“”'‘’]([^"“”'‘’]{2,80})["“”'‘’]/gu;
function classifyAssistantTask(input) {
    const text = input.replace(/\s+/g, ' ').trim();
    const mutation = hasMutationAction(text);
    const readOnly = READ_ONLY_PATTERN.test(text) && !mutation;
    const domain = resolveDomain(text);
    const intent = resolveIntent(text, readOnly);
    const target = resolveTarget(text, domain);
    const risk = readOnly
        ? 'read'
        : intent === 'publish' || intent === 'fix'
            ? 'high_write'
            : 'low_write';
    const durableTargetCandidate = !readOnly &&
        intent !== 'list' &&
        ((domain === 'automation' && shouldUseAutomationHarness(text)) ||
            (domain === 'custom_app' && mutation));
    return {
        ...(domain ? { domain } : {}),
        intent,
        risk,
        target,
        confidence: domain ? (durableTargetCandidate ? 0.86 : 0.88) : 0.7,
        readOnly,
        durableTargetCandidate,
    };
}
function isFocusedAttachedDatabaseSchemaEdit(input) {
    const normalized = input.toLowerCase().replace(/\s+/g, ' ').trim();
    const mentionsColumns = /\b(?:column|columns|spalte|spalten|feld|felder)\b/u.test(normalized);
    const mutatesColumns = /\b(?:add|create|insert|rename|remove|delete|convert|type|ergänz|ergaenz|hinzufüg|hinzufueg|erstell|umbenenn|entfern|lösch|loesch|konvertier|typisier)\w*/u.test(normalized);
    return mentionsColumns && mutatesColumns;
}
function resolveDomain(text) {
    const explicitTargetDomain = resolveExplicitTargetDomain(text);
    if (explicitTargetDomain)
        return explicitTargetDomain;
    if (CUSTOM_APP_LIST_PATTERN.test(text))
        return 'custom_app';
    if (AUTOMATION_PATTERN.test(text))
        return 'automation';
    if (CUSTOM_APP_PATTERN.test(text))
        return 'custom_app';
    return undefined;
}
function resolveExplicitTargetDomain(text) {
    for (const match of text.matchAll(EXPLICIT_TARGET_ID_PATTERN)) {
        const type = targetTypeForKey(match[1]);
        if (type === 'custom_app' || type === 'app_block')
            return 'custom_app';
        if (type === 'automation')
            return 'automation';
    }
    return undefined;
}
function resolveIntent(text, readOnly) {
    if (CUSTOM_APP_LIST_PATTERN.test(text))
        return 'list';
    if (readOnly)
        return 'inspect';
    if (PUBLISH_PATTERN.test(text))
        return 'publish';
    if (/\b(fix|reparier\w*)\b/iu.test(text))
        return 'fix';
    if (TEST_PATTERN.test(text))
        return 'test';
    if (BIND_PATTERN.test(text))
        return 'bind';
    if (/\bedit\b/iu.test(text) || GERMAN_EDIT_PATTERN.test(text))
        return 'edit';
    return 'create';
}
function shouldUseAutomationHarness(text) {
    const mentionsAutomation = AUTOMATION_PATTERN.test(text);
    const mutation = hasMutationAction(text);
    const asksForMutation = (mutation && AUTOMATION_FLOW_PATTERN.test(text)) ||
        (/\bautomatisier/iu.test(text) && AUTOMATION_FLOW_PATTERN.test(text)) ||
        (mentionsAutomation && mutation);
    return ((mentionsAutomation || /\bautomatisier\w*/iu.test(text)) && asksForMutation);
}
function hasMutationAction(text) {
    return MUTATION_PATTERN.test(text) || GERMAN_EDIT_PATTERN.test(text);
}
function resolveTarget(text, domain) {
    const refs = [];
    for (const match of text.matchAll(EXPLICIT_TARGET_ID_PATTERN)) {
        refs.push({
            type: targetTypeForKey(match[1]),
            id: match[2],
            source: 'explicit_id',
        });
    }
    for (const match of text.matchAll(UUID_PATTERN)) {
        if (refs.some(ref => ref.id === match[0]))
            continue;
        refs.push({
            type: domain === 'automation' ? 'automation' : 'unknown',
            id: match[0],
            source: 'explicit_id',
        });
    }
    for (const match of text.matchAll(QUOTED_TARGET_PATTERN)) {
        refs.push({
            type: domain === 'custom_app' ? 'custom_app' : 'unknown',
            label: match[1].trim(),
            source: 'quoted_text',
        });
    }
    if (refs.length === 0 && domain) {
        refs.push({ type: domain, source: 'domain_mention' });
    }
    return { refs };
}
function targetTypeForKey(key) {
    if (key === 'customAppId')
        return 'custom_app';
    if (key === 'appBlockId')
        return 'app_block';
    if (key === 'databaseId')
        return 'database';
    if (key === 'automationId')
        return 'automation';
    if (key === 'docId' || key === 'documentId')
        return 'document';
    return 'unknown';
}
//# sourceMappingURL=index.js.map